import { useEffect, useRef, useState } from "react";

import { Icon, type IconName } from "./Icon";

interface MenuItem {
  label: string;
  icon: IconName;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

function MoreMenu({ items }: { items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", escape, true);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", escape, true);
    };
  }, [open]);

  return (
    <div className="menu-root" ref={rootRef}>
      <button
        className={`icon-button ${open ? "is-active" : ""}`}
        type="button"
        aria-label="その他のメニュー"
        aria-haspopup="menu"
        aria-expanded={open}
        title="その他"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="more" size={20} />
      </button>
      {open && (
        <div className="menu" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              className={`menu-item ${item.danger ? "is-danger" : ""}`}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              <Icon name={item.icon} size={17} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface TopBarProps {
  fileName?: string;
  imageCount: number;
  canUndo: boolean;
  canRedo: boolean;
  preview: boolean;
  busy?: boolean;
  hasEdits: boolean;
  onHome: () => void;
  onAddImages: () => void;
  onDiscardAll: () => void;
  onShowShortcuts: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onTogglePreview: () => void;
  onExport: () => void;
}

export function TopBar({
  fileName,
  imageCount,
  canUndo,
  canRedo,
  preview,
  busy,
  hasEdits,
  onHome,
  onAddImages,
  onDiscardAll,
  onShowShortcuts,
  onUndo,
  onRedo,
  onTogglePreview,
  onExport,
}: TopBarProps) {
  const hasImages = imageCount > 0;
  return (
    <header className="top-bar">
      <button
        className="brand-lockup"
        type="button"
        title={hasImages ? "最初の画面に戻る" : "Mosaic Studio"}
        aria-label={hasImages ? "最初の画面に戻る" : "Mosaic Studio"}
        disabled={!hasImages}
        onClick={onHome}
      >
        <Icon name="brand" size={20} />
        <span>Mosaic Studio</span>
      </button>

      {hasImages && (
        <div className="history-buttons">
          <button
            className="icon-button"
            type="button"
            aria-label="元に戻す"
            title="元に戻す (Ctrl/Cmd+Z)"
            disabled={!canUndo || busy}
            onClick={onUndo}
          >
            <Icon name="undo" size={19} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="やり直す"
            title="やり直す (Ctrl/Cmd+Shift+Z)"
            disabled={!canRedo || busy}
            onClick={onRedo}
          >
            <Icon name="redo" size={19} />
          </button>
        </div>
      )}

      <div className="file-name" title={fileName}>
        {fileName}
      </div>

      {hasImages && (
        <div className="top-actions">
          <button
            className={`toolbar-button ${preview ? "is-active" : ""}`}
            type="button"
            aria-pressed={preview}
            title="枠やハンドルを隠して仕上がりを確認 (P)"
            onClick={onTogglePreview}
          >
            <Icon name="eye" size={18} />
            <span>プレビュー</span>
          </button>
          <MoreMenu
            items={[
              { label: "画像を追加", icon: "plus", onSelect: onAddImages },
              {
                label: "キーボードショートカット",
                icon: "keyboard",
                onSelect: onShowShortcuts,
              },
              {
                label: "すべての加工を取り消す",
                icon: "refresh",
                danger: true,
                disabled: !hasEdits || busy,
                onSelect: onDiscardAll,
              },
              { label: "最初の画面に戻る", icon: "home", danger: true, onSelect: onHome },
            ]}
          />
          <button
            className="primary-button"
            type="button"
            title="ダウンロード (Ctrl/Cmd+S)"
            disabled={busy}
            onClick={onExport}
          >
            <Icon name="download" size={17} />
            <span>ダウンロード</span>
          </button>
        </div>
      )}
    </header>
  );
}
